
import React from "react";
import { z } from "zod";
import { clientFormSchema } from "@/contexts/ProgramCreationContext";
import { Client } from "@/components/programs/ClientSelection";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { ClientDetailsForm } from "./ClientDetailsForm";
import { ActionButtons } from "./ActionButtons";

type ClientDetailsWrapperProps = {
  client: Client;
  onUpdateClientDetails: (data: z.infer<typeof clientFormSchema>) => Promise<void>;
  isSaving: boolean;
  onContinue: () => void;
};

export const ClientDetailsWrapper = ({
  client,
  onUpdateClientDetails,
  isSaving,
  onContinue
}: ClientDetailsWrapperProps) => {
  const handleSubmit = (data: z.infer<typeof clientFormSchema>) => {
    onUpdateClientDetails(data);
  };

  // Create a properly typed version of the client for saving
  const handleSave = () => {
    // Convert client data to match the expected schema type
    const clientData: z.infer<typeof clientFormSchema> = {
      name: client.name,
      email: client.email || "",
      phone: client.phone || "",
      age: client.age,
      // Ensure gender is of the correct type
      gender: (client.gender as "male" | "female" | "other") || "other",
      goals: client.goals || "",
      notes: client.notes || ""
    };
    
    onUpdateClientDetails(clientData);
  };

  return (
    <Card className="backdrop-blur-lg bg-white/10 border-white/20">
      <CardHeader>
        <CardTitle className="text-white">Client Details</CardTitle>
        <CardDescription className="text-white/70">
          Review and update client information before creating a program
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ClientDetailsForm client={client} onSubmit={handleSubmit} />
      </CardContent>
      <CardFooter>
        <ActionButtons 
          onSave={handleSave}
          onContinue={onContinue}
          isSaving={isSaving}
        />
      </CardFooter>
    </Card>
  );
};
